import search from 'app/_shared/events/search'
import { getFirstItem } from 'lib/util'

const SCALING_FACTOR = 1000
const MEASUREMENT_KEY = 'measurement.latest'
const LATEST_DATE_KEY = 'date.latest'

/**
 * If `number` is defined, divides it by `factor`. Otherwise, returns 0.
 *
 * @param number      value to scale
 * @param [factor]    scaling factor
 * @param [decimals]  decimal places to display
 */
const scale = (
  number: number,
  factor: number = SCALING_FACTOR,
  decimals: number = factor.toString().length,
) => number
  ? parseFloat((number / factor).toFixed(decimals))
  : 0

/**
 * Switches little-endian to big-endian.
 *
 * Peripheral sends data in little-endian byte order despite the spec stating big-endian.
 *
 * @param hex
 */
const switchEndian = (hex: string) => {
  return hex.match(/../g)?.reverse().join('') || '';
}

/**
 * Switches `data` endianness.
 *
 * @param data
 */
const parseFile = (data: string) => {
  return data.match(/.{8}/g)?.map((hex) => parseInt(switchEndian(hex), 16))
}

/**
 * Generates a linearly spaced vector with values between `start` and `end`, distributing them
 * evenly.
 *
 * @param start initial vector value
 * @param end   final vector value
 * @param num   step value
 */
const linspace = (start: number, end: number, num: number) => {
  if (num <= 0) return [];
  if (num === 1) return [start];

  const step = (end - start) / (num - 1);
  return Array.from({ length: num }, (_, i) => start + i * step);
}

/**
 * Returns the arithmetic average of elements in an `array` of numbers.
 *
 * @param array target
 */
const mean = (array: number[]) => {
  if (!array?.length) {
    return 0
  }

  return array.reduce((acc, item) => {
    return acc + item
  }, 0) / array.length
}

/**
 * Calculates the standard deviation of array elements, measuring how spread out the values are from
 * the mean.
 *
 * @param array target
 */
const standardDeviation = (array: number[]) => {
  // Obtaining array's mean
  const meanValue = mean(array)
  // Assigning (value - 2) ^ 2 to every item
  const updated = array.map(item => (item - meanValue) ** 2)
  // Calculating sum of updated array
  const sum = updated.reduce((acc, item) => acc + item, 0)
  // Calculating variance
  const variance = sum / updated.length
  // Returning standard deviation
  return Math.sqrt(variance)
}

/**
 * Computes normalized tick values between `min` and `max` by `targetCount` increments.
 *
 * @param min         lowest range value
 * @param max         highest range value
 * @param targetCount increment amount
 */
const computeTickValues = (min: number, max: number, targetCount = 7) => {
  const range = max - min
  if (range <= 0) return [min]

  const rawStep = range / targetCount
  const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)))
  const normalized = rawStep / magnitude

  let niceStep: number
  if (normalized <= 1.5) niceStep = 1
  else if (normalized <= 3) niceStep = 2
  else if (normalized <= 7) niceStep = 5
  else niceStep = 10;

  const step = niceStep * magnitude
  const start = Math.ceil(min / step) * step

  const ticks: number[] = [];
  for (let v = start; v <= max; v += step) {
    ticks.push(v)
  }

  return ticks;
}

/**
 * Returns the current date only if the last saved date is earlier; otherwise, it throws an error.
 *
 * @param module  current module
 * @throws Error  if current date is older than the last saved date
 */
const getMaxSeenDate = (module: Gaia.AppModule.Spec) => {
  const storage = module[G.ADAPTER][G.STORAGE]
  const today = new Date()
  const maxSeen = storage.get(LATEST_DATE_KEY)
  const maxSeenDate = maxSeen && new Date(maxSeen)

  if (maxSeenDate && today.getTime() < maxSeenDate.getTime()) {
    throw Error(`Date rollback detected: ${today} < ${maxSeenDate}`)
  }

  storage.set(LATEST_DATE_KEY, today.toISOString())

  return today.toISOString().split('T')[0]
}

/**
 * Attempts to obtain a sensor by its `serial`.
 *
 * @param module  current module
 * @param serial  identifier of the sensor to search for
 */
const searchSensor = async (module: Gaia.AppModule.Spec, serial: string) => {
  const filter = { serial }
  const type = 'x_eco_apmsensor'
  const sort = [["submit_timestamp<number>", "desc"]]
  const options = { type, filter, sort }

  try {
    const sensors = await search(null, module, null, options)
    return getFirstItem(sensors)

  } catch (cause) {
    throw new Error('Unable to set sensor id from pairing model', { cause })
  }
}

const helpers = {
  scale,
  parseFile,
  linspace,
  mean,
  standardDeviation,
  computeTickValues,
  getMaxSeenDate,
  searchSensor,
  MEASUREMENT_KEY,
}

export {
  helpers as default,
  scale,
  parseFile,
  linspace,
  mean,
  standardDeviation,
  computeTickValues,
  getMaxSeenDate,
  searchSensor,
  MEASUREMENT_KEY,
}
